import { ATTACHMENT_NODE_TYPE, attachmentAsText, type AttachmentNote } from "./attachments";
import { NODE_STATE_KEY, BLOCK_ID_KEY } from "./block-id";
import { CORE_REGISTRY } from "./capabilities";
import { DEFAULT_LIMITS, tableLimits, type DocumentLimits } from "./limits";
import {
  flattenTable,
  flattenToParagraphs,
  normalizeTable,
  TABLE_CELL_NODE_TYPE,
  TABLE_NODE_TYPE,
  type TableNote,
} from "./tables";
import { CORE_TOKEN_REGISTRY } from "./tokens";
import type { DocumentId, SerializedNodeLike } from "./types";
import { checkUrl, DEFAULT_URL_POLICY, type UrlPolicy } from "./url-policy";
import { walkNodes, type DocumentIssue, type LimitName, type ValidateOptions } from "./validate";

/** The clipboard type z-editor writes and prefers. Other applications get HTML and plain text. */
export const CLIPBOARD_MIME_TYPE = "application/vnd.z-editor+json";
export const CLIPBOARD_FORMAT = "z-editor-clipboard";
export const CLIPBOARD_VERSION = 1;

export interface ClipboardPayload {
  format: typeof CLIPBOARD_FORMAT;
  /** Ours, like `schemaVersion`. A reader declines versions it does not know. */
  version: number;
  /** The document the content was copied from; `null` when the writer had none. */
  documentId: DocumentId | null;
  nodes: SerializedNodeLike[];
}

export interface FragmentOptions extends ValidateOptions {
  urlPolicy?: UrlPolicy;
  /** The document the content is going into. */
  documentId: DocumentId;
  /**
   * Whether the editor the content is going into has tables. Where it has none (`false`), a table
   * comes in as lines of text, a row each, instead of being declined. Default `true`.
   */
  tables?: boolean;
  /**
   * Whether an attachment may stand in the document the content is going into. Where it may not
   * (`false`: an editor with no attachments, or a composer, which keeps them beside the message),
   * one comes in as its name. Default `true`.
   */
  attachments?: boolean;
}

/** Something the gate changed so that the content fits, which the user should be told about. */
export type FragmentNote = TableNote | AttachmentNote;

export type FragmentResult =
  /**
   * Checked and cleaned content, and how deep it nests. Safe to hand to the editor. `notes` lists
   * what had to change for it to fit; no text is ever among the changes.
   */
  | { status: "ok"; nodes: SerializedNodeLike[]; depth: number; notes: readonly FragmentNote[] }
  /** Not usable, by no fault of its size: forged, malformed, or newer. Try the next format. */
  | { status: "declined"; issue: DocumentIssue }
  /** Over a limit. Every other format of the same content would be too, so nothing is used. */
  | { status: "too-large"; limit: LimitName; max: number };

/** The `rel` Lexical's `$toggleLink` gives a new link. */
const PASTED_LINK_REL = "noreferrer";

const encoder = new TextEncoder();

/** UTF-8 size against a limit. UTF-8 needs at least one byte per UTF-16 unit, so length settles most cases. */
export const exceedsBytes = (text: string, maxBytes: number): boolean =>
  text.length > maxBytes || encoder.encode(text).length > maxBytes;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const declined = (issue: DocumentIssue): FragmentResult => ({ status: "declined", issue });

export function writeClipboardPayload(
  documentId: DocumentId | null,
  nodes: readonly SerializedNodeLike[],
): string {
  const payload: ClipboardPayload = {
    format: CLIPBOARD_FORMAT,
    version: CLIPBOARD_VERSION,
    documentId,
    nodes: [...nodes],
  };
  return JSON.stringify(payload);
}

/**
 * Reads the internal clipboard type. Another application can put anything under that type, so it
 * is treated exactly like stored content: size first, then the envelope, then every node.
 */
export function readClipboardPayload(text: string, options: FragmentOptions): FragmentResult {
  const limits = options.limits ?? DEFAULT_LIMITS;
  if (exceedsBytes(text, limits.maxBytes)) {
    return { status: "too-large", limit: "bytes", max: limits.maxBytes };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return declined({
      code: "invalid-json",
      message: error instanceof Error ? error.message : "Invalid JSON.",
    });
  }
  if (!isRecord(parsed) || parsed.format !== CLIPBOARD_FORMAT) {
    return declined({ code: "not-a-document", message: `Not a ${CLIPBOARD_FORMAT} payload.` });
  }
  if (
    typeof parsed.version !== "number" ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 1
  ) {
    return declined({ code: "invalid-envelope", message: "version is missing." });
  }
  if (parsed.version > CLIPBOARD_VERSION) {
    return declined({
      code: "future-version",
      message: `Clipboard version ${parsed.version} is newer than ${CLIPBOARD_VERSION}.`,
    });
  }
  if (parsed.documentId !== null && typeof parsed.documentId !== "string") {
    return declined({ code: "invalid-envelope", message: "documentId is not a string or null." });
  }
  return checkFragment(parsed.nodes, { ...options, sourceDocumentId: parsed.documentId });
}

/**
 * The gate every paste and import goes through, whatever it started as: the node walk a stored
 * document gets, then links the URL policy rejects are unwrapped, and block IDs are removed unless
 * the content came from the document it is going into.
 */
export function checkFragment(
  nodes: unknown,
  options: FragmentOptions & { sourceDocumentId: DocumentId | null },
): FragmentResult {
  const limits: DocumentLimits = options.limits ?? DEFAULT_LIMITS;
  if (!Array.isArray(nodes)) {
    return declined({ code: "invalid-envelope", message: "nodes is not a list." });
  }
  const walk = walkNodes(nodes, {
    registry: options.registry ?? CORE_REGISTRY,
    tokens: options.tokens ?? CORE_TOKEN_REGISTRY,
    limits,
    fragment: true,
  });
  if (walk.issue !== null) {
    const limit = walk.issue.limit;
    if (walk.issue.code !== "limit-exceeded" || limit === undefined || limit === "bytes") {
      return declined(walk.issue);
    }
    const table = tableLimits(limits);
    const max =
      limit === "nodes"
        ? limits.maxNodes
        : limit === "depth"
          ? limits.maxDepth
          : // One number for a limit that has two: the cells a table may have.
            table.rows * table.columns;
    return { status: "too-large", limit, max };
  }
  const notes = new Set<FragmentNote>();
  const clean = cleaner(
    options.urlPolicy ?? DEFAULT_URL_POLICY,
    options.sourceDocumentId !== null && options.sourceDocumentId === options.documentId,
    options.tables !== false,
    options.attachments !== false,
    notes,
  );
  return {
    status: "ok",
    nodes: (nodes as SerializedNodeLike[]).flatMap((node) => clean(node, null)),
    depth: walk.depth,
    notes: [...notes],
  };
}

/** Copies as it goes; the walk above has already bounded the depth, so recursion is safe. */
function cleaner(
  urlPolicy: UrlPolicy,
  keepBlockIds: boolean,
  tables: boolean,
  attachments: boolean,
  notes: Set<FragmentNote>,
): (node: SerializedNodeLike, parent: string | null) => SerializedNodeLike[] {
  const clean = (node: SerializedNodeLike, parent: string | null): SerializedNodeLike[] => {
    const children = node.children?.flatMap((child) => clean(child, node.type));
    if (node.type === "link" || node.type === "autolink") {
      // A link the policy rejects keeps its text and loses its target.
      if (!checkUrl(String(node.url), urlPolicy).ok) return children ?? [];
      // What the editor gives a link it makes itself. Whatever a paste brought (`opener`, a named
      // `target`) is somebody else's choice, and the static renderer decides its own anyway.
      return [{ ...node, rel: PASTED_LINK_REL, target: null, children: children ?? [] }];
    }
    // An attachment stands at the top level, like a table. Anywhere else, and in an editor that
    // has none, what is kept is its name: inside a cell `normalizeTable` sees to that, inside
    // any other block it becomes that block's text, and at the top level a paragraph.
    if (
      node.type === ATTACHMENT_NODE_TYPE &&
      parent !== TABLE_CELL_NODE_TYPE &&
      !(parent === null && attachments)
    ) {
      notes.add("attachment-flattened");
      const label = attachmentAsText(node);
      if (label === null) return [];
      return parent === null ? [{ type: "paragraph", version: 1, children: [label] }] : [label];
    }
    let next: SerializedNodeLike = children === undefined ? { ...node } : { ...node, children };
    // The editor writes no inline styles of its own, and one that came in is applied as CSS by the
    // engine: an overlay over the host's page, or a background fetched from anywhere.
    if (typeof next.style === "string" && next.style !== "") next.style = "";
    if (node.type === TABLE_NODE_TYPE) {
      // A table stands at the top level or nowhere. Inside a cell, `normalizeTable` on the outer
      // table turns it into that cell's text; inside anything else (a list item, a quote) it
      // becomes lines of that block, because those hold text and not blocks.
      if (parent !== null && parent !== TABLE_CELL_NODE_TYPE) {
        notes.add("nested-table-flattened");
        return flattenToParagraphs(next, new Set()).flatMap((line, index) => [
          ...(index > 0 ? [{ type: "linebreak", version: 1 } as SerializedNodeLike] : []),
          ...(line.children ?? []),
        ]);
      }
      if (parent === null) {
        if (!tables) return flattenTable(next, notes);
        next = normalizeTable(next, notes);
      }
    }
    const state = next[NODE_STATE_KEY];
    if (!keepBlockIds && isRecord(state) && BLOCK_ID_KEY in state) {
      const { [BLOCK_ID_KEY]: _dropped, ...rest } = state;
      if (Object.keys(rest).length === 0) delete next[NODE_STATE_KEY];
      else next[NODE_STATE_KEY] = rest;
    }
    return [next];
  };
  return clean;
}
